import { useWSSnackbar, WSTable } from "@wingspanhq/fe-component-library";
import { PayerRow } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayers } from "@query";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { PAYERS_PATH } from "../../paths";
import { getColumns } from "./columns";

/**
 * One placeholder row is enough to reach `WSTable`'s loading branch — its
 * `Loading` component takes only `columns` and generates its own skeleton rows,
 * so this count deliberately does NOT mirror the library's.
 */
const LOADING_PLACEHOLDER_ROW = [
  { id: "loading", data: {} as PayerRow }
] as const;

type Props = {
  payers: PayerRow[];
  loading?: boolean;
  /**
   * Archived (Deactivated) clients list — adds the "Restore client" row action,
   * the only action the clients table has ever carried.
   */
  isArchivedOnly?: boolean;
};

export const TablePayers: React.FC<Props> = props => {
  const history = useHistory();
  const { openSnackbar } = useWSSnackbar();

  // Restore reactivates a deactivated payer (v3 `restorePayer`, WIN-15189). Its
  // registry entry invalidates the client search rows, so the restored client
  // drops off the archived list and reappears on the active one.
  const [restoreClient] = queriesPayers.restorePayer.useMutation();

  // `WSTable` draws its own shimmer rows for `loading`, but only reaches that
  // branch when it has rows — an empty `tableData` renders an empty container
  // and the list reads as blank while the first page is in flight. A single
  // placeholder row gets past that; its `data` is never read, because `loading`
  // makes the table render skeleton cells instead of the columns' render
  // functions.
  const tableData = useMemo(
    () =>
      props.loading && props.payers.length === 0
        ? [...LOADING_PLACEHOLDER_ROW]
        : props.payers.map(payer => ({
            id: payer.id as string,
            data: payer
          })),
    [props.loading, props.payers]
  );

  const columns = useMemo(() => getColumns(), []);

  // Every clients surface (list, archived, search) navigates to the same detail
  // route, so the table owns navigation — as the twin TablePayees does.
  const getDetailsPath = (payerId: string) => `${PAYERS_PATH}/${payerId}`;

  return (
    <WSTable<PayerRow>
      data-testid="clientsTable"
      loading={props.loading}
      tableData={tableData}
      columns={columns}
      onRowClick={item => history.push(getDetailsPath(item.id))}
      onRowControlClick={item => openInNewTab(getDetailsPath(item.id))}
      onRowMousewheelClick={item => openInNewTab(getDetailsPath(item.id))}
      rowMenuActions={
        props.isArchivedOnly
          ? item => [
              {
                label: "Restore client",
                icon: "repeat-circle",
                onAsyncClick: async () => {
                  await restoreClient(
                    { payerId: item.id },
                    {
                      onSuccess: () => {
                        openSnackbar({
                          duration: 5000,
                          type: "success",
                          message: "Client restored successfully"
                        });
                      },
                      onError: () => {
                        openSnackbar({
                          duration: 5000,
                          type: "warning",
                          message: "Failed to restore client"
                        });
                      }
                    }
                  );
                }
              }
            ]
          : undefined
      }
    />
  );
};
